// src/App.js
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { bootstrapSession } from "./components/auth/bootstrapSession";
import Login from "./components/login/login";
import TopBar from "./components/topBar";
import { DataViewProvider } from "./contexts/data.view.context";
import MuiThemeWrapper from "./muiThemeProvider";
import { createTheme } from "@mui/material/styles";
import { NotificationContainer } from "react-notifications";

// ✅ NEW: inject MSAL context into API util so it can renew token on expiry
import { setMsalContext } from "./components/api";
import ErrorBoundary from "./components/ErrorBoundary";

import UnifiedEventSummary from "./components/reports/unifiedEventSummary";
import FileTransfers from "./components/fileTransfer/index";
import EIConnections from "./components/eiConnection";
import OuraPatients from "./components/ouraPatient";
import APIConfigs from "./components/apiConfig";
import ServiceConfigs from "./components/serviceConfig";
import TaskSchedules from "./components/taskSchedule";
import EICaches from "./components/eiCache";
import RemarqueEvents from "./components/remarqueEvents";
import QueryInfo from "./components/queryInfo";
import RmqApiUploadHistoryView from "./components/rmqApiUploadHistoryView";
import FileTransferHistoryView from "./components/fileTransferHistory";
import FileTrnsStatsViewByTrnsID from "./components/fileTransferStats/transferID";
import FileTrnsStatsViewByGroup from "./components/fileTransferStats/group";
import FileTrnsStatsViewByProject from "./components/fileTransferStats/projectID";
import ErrorLogReport from "./components/reports/errorLogReport";
import EventsReport from "./components/reports/eventsReport";
import ReceiveDataReport from "./components/reports/recieveDataReport";
import ErrorLogDetails from "./components/errorLogDetals";
import RedHatProcess from "./components/redHatProcess";
import FileCommands from "./components/fileTransferCommand";
import ConnectionCommands from "./components/connectionCommand";
import QueryScheduleTask from "./components/queryScheduleTask";
import EIConnectionForm from "./components/eiConnection/eiConnectionForm";
import FileTransferCommandForm from "./components/fileTransferCommand/fileTransferCommandForm";
import ConnectionCommandForm from "./components/connectionCommand/connectionCommandForm";
import OuraPatientForm from "./components/ouraPatient/ouraPatientForm";
import FileTransferForm from "./components/fileTransfer/fileTransferForm";
import APIConfigForm from "./components/apiConfig/apiConfigForm";
import ServiceConfigForm from "./components/serviceConfig/serviceConfigForm";
import TaskScheduleForm from "./components/taskSchedule/taskScheduleForm";
import QueryScheduleTaskForm from "./components/queryScheduleTask/queryScheduleTaskForm";
import ErrorsBySourceEntity from "./components/reports/ErrorsBySourceEntity";
import UnifiedFileSummary from "./components/reports/unifiedFileSummary";
import UnifiedBatchProcessSummary from "./components/reports/unifiedBatchSummary";
import UnifiedJobRunSummary from "./components/reports/unifiedJobRunSummary";

// Previously `createTheme()` with no arguments — the ThemeProvider below
// existed but contributed nothing, so every screen styled itself ad hoc
// with inline sx/hex colors. This gives the app one real palette/shape/
// typography to draw from; see UI_POLISH_AUDIT.md for the full rationale.
const theme = createTheme({
  palette: {
    primary: { main: "#2B73B0" },
    secondary: { main: "#5F9EA0" },
    success: { main: "#2BB041" },
    warning: { main: "#E67E22" },
    background: { default: "#F7F8FA" },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        // overflow: hidden so the tinted DialogTitle bar (see appDialog.js)
        // respects these rounded corners instead of squaring off at top.
        paper: { borderRadius: 12, overflow: "hidden" },
      },
    },
    // Default Dialog/Modal backdrop is a flat black at 50% opacity — the
    // "black background" behind every info modal. Softened to a lighter,
    // slightly blue-grey tint so the dialog stands out without the rest
    // of the screen going fully dark.
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: "rgba(30, 41, 59, 0.35)" },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: { textTransform: "none" },
      },
    },
  },
});

function LoadingScreen({ text = "Loading..." }) {
  return (
    <div style={{ padding: 24 }}>
      <h3>{text}</h3>
    </div>
  );
}

export default function App() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [bootstrapped, setBootstrapped] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    if (instance) {
      setMsalContext({ instance });
    }
  }, [instance]);

  useEffect(() => {
    if (!instance) return;

    const active = instance.getActiveAccount();
    if (!active && accounts.length === 1) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance, accounts]);

  useEffect(() => {
    setIsSignedIn(!!isAuthenticated);
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!isAuthenticated) {
        setBootstrapped(false);
        return;
      }
      if (inProgress !== InteractionStatus.None) return;

      try {
        const result = await bootstrapSession({ instance });
        if (!cancelled && result?.ready) setBootstrapped(true);
      } catch (e) {
        console.error("bootstrapSession failed:", e);
        if (!cancelled) setBootstrapped(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, inProgress, instance]);

  // ✅ Gate while MSAL is busy
  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen text="Finalizing sign-in..." />;
  }

  // ✅ Not authenticated -> show Login (redirect)
  if (!isAuthenticated) {
    return <Login setIsSignedIn={setIsSignedIn} />;
  }

  // ✅ Authenticated but session not bootstrapped yet -> wait
  if (!bootstrapped) {
    return <LoadingScreen text="Preparing session..." />;
  }

  // ✅ Safe to render app
  return (
    <ErrorBoundary>
    <MuiThemeWrapper theme={theme}>
      <DataViewProvider>
        <Router>
          <TopBar isSignedIn={isSignedIn} setIsSignedIn={setIsSignedIn} />

          <Routes>
            <Route path="/" element={<UnifiedEventSummary />} />
            <Route
              path="/UnifiedEventSummary"
              element={<UnifiedEventSummary />}
            />

            <Route
              path="/RmqApiUploadHistoryView"
              element={<RmqApiUploadHistoryView />}
            />
            <Route
              path="/FileTransferHistoryView"
              element={<FileTransferHistoryView />}
            />
            <Route
              path="/FileTrnsStatsViewByTrnsID"
              element={<FileTrnsStatsViewByTrnsID />}
            />

            <Route path="/FileTransfers" element={<FileTransfers />} />
            <Route path="/EIConnection" element={<EIConnections />} />
            <Route path="/OuraPatients" element={<OuraPatients />} />
            <Route path="/APIConfigs" element={<APIConfigs />} />
            <Route path="/ServiceConfigs" element={<ServiceConfigs />} />
            <Route path="/TaskSchedules" element={<TaskSchedules />} />
            <Route path="/EICaches" element={<EICaches />} />
            <Route path="/RemarqueEvents" element={<RemarqueEvents />} />
            <Route path="/QueryInfo" element={<QueryInfo />} />

            <Route
              path="/FileTrnsStatsViewByGroup"
              element={<FileTrnsStatsViewByGroup />}
            />
            <Route
              path="/FileTrnsStatsViewByProject"
              element={<FileTrnsStatsViewByProject />}
            />

            <Route path="/ErrorLogReport" element={<ErrorLogReport />} />
            <Route path="/EventsReport" element={<EventsReport />} />
            <Route path="/ReceiveDataReport" element={<ReceiveDataReport />} />
            <Route path="/ErrorLogDetails" element={<ErrorLogDetails />} />

            <Route path="/RedHatProcess" element={<RedHatProcess />} />

            <Route
              path="/FileCommands/:transferID"
              element={<FileCommands />}
            />
            <Route
              path="/ConnectionCommands/:connectionID"
              element={<ConnectionCommands />}
            />
            <Route
              path="/QueryScheduleTasks/:schedule_id"
              element={<QueryScheduleTask />}
            />

            <Route
              path="/UnifiedFileSummary"
              element={<UnifiedFileSummary />}
            />

            <Route
              path="/UnifiedBatchProcessSummary"
              element={<UnifiedBatchProcessSummary />}
            />
            <Route
              path="/UnifiedJobRunSummary"
              element={<UnifiedJobRunSummary />}
            />
            <Route path="/AddEIConnection" element={<EIConnectionForm />} />
            <Route
              path="/AddTransferCmd"
              element={<FileTransferCommandForm />}
            />
            <Route
              path="/AddConnectionCmd"
              element={<ConnectionCommandForm />}
            />
            <Route path="/AddOuraPatient" element={<OuraPatientForm />} />
            <Route path="/AddFileTransfer" element={<FileTransferForm />} />
            <Route path="/AddServiceConfig" element={<ServiceConfigForm />} />
            <Route path="/AddAPIConfig" element={<APIConfigForm />} />
            <Route path="/AddQuerySchedule" element={<TaskScheduleForm />} />
            <Route
              path="/AddQueryScheduleTask"
              element={<QueryScheduleTaskForm />}
            />

            <Route
              path="/IntegrationErrors/:Source/:Entity/:Period"
              element={<ErrorsBySourceEntity type="integration" />}
            />
            <Route
              path="/KickoutErrors/:Source/:Entity/:Period"
              element={<ErrorsBySourceEntity type="kickout" />}
            />
          </Routes>

          <NotificationContainer />
        </Router>
      </DataViewProvider>
    </MuiThemeWrapper>
    </ErrorBoundary>
  );
}
